import React, { Suspense, lazy, useEffect, useState } from 'react';

import { getDesktopOfflineMapStatus } from '@/api/offlineMap';


const OfflinePmtilesDispatchMap = lazy(() => import('./OfflinePmtilesDispatchMap'));

const unavailableMessage = 'Offline map is not installed.';
const unavailableDetail = 'Map-based dispatch is unavailable until the offline map is installed.';

const reasonLabels = {
  missing_manifest: 'The offline map manifest is missing.',
  invalid_manifest: 'The offline map manifest is invalid.',
  missing_file: 'The offline map file is missing.',
  unreadable_manifest: 'The offline map manifest could not be read.',
  local_backend_unavailable: 'The local desktop backend is unavailable.',
};

function OfflineMapUnavailable({ status }) {
  const reason = status?.reason ? reasonLabels[status.reason] || status.reason : null;
  return (
    <div
      className="flex h-full w-full items-center justify-center bg-stone-100 p-6"
      data-testid="offline-map-unavailable"
    >
      <div className="max-w-md rounded-2xl border border-orange-200 bg-orange-50 p-5 text-center shadow-soft-sm">
        <p className="text-base font-semibold text-orange-950">{unavailableMessage}</p>
        <p className="mt-2 text-sm text-orange-900">{unavailableDetail}</p>
        {reason && (
          <p className="mt-3 rounded-xl bg-white/70 px-3 py-2 text-xs text-orange-800">
            {reason}
          </p>
        )}
      </div>
    </div>
  );
}

const OfflineDispatchMapSurface = ({
  orders = [],
  selectedRun = null,
  driverColor = null,
  actionScope,
  actionBlocked,
  onPlaceOrder,
  onUnplaceOrder,
  onRouteInfoChange,
}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadStatus() {
      setLoading(true);
      try {
        const nextStatus = await getDesktopOfflineMapStatus();
        if (!cancelled) setStatus(nextStatus);
      } catch (error) {
        if (!cancelled) {
          setStatus({
            installed: false,
            reason: 'status_check_failed',
            detail: error?.message || 'Offline map status could not be checked.',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadStatus();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-stone-100" data-testid="offline-map-loading">
        <div className="rounded-2xl border border-stone-200 bg-white px-5 py-4 text-sm text-stone-600 shadow-soft-sm">
          Checking offline map...
        </div>
      </div>
    );
  }

  if (!status?.installed) {
    return <OfflineMapUnavailable status={status} />;
  }

  return (
    <Suspense
      fallback={(
        <div className="flex h-full w-full items-center justify-center bg-stone-100" data-testid="offline-map-loading">
          <div className="rounded-2xl border border-stone-200 bg-white px-5 py-4 text-sm text-stone-600 shadow-soft-sm">
            Loading offline map...
          </div>
        </div>
      )}
    >
      <OfflinePmtilesDispatchMap
        mapStatus={status}
        orders={orders}
        selectedRun={selectedRun}
        driverColor={driverColor}
        actionScope={actionScope}
        actionBlocked={actionBlocked}
        onPlaceOrder={onPlaceOrder}
        onUnplaceOrder={onUnplaceOrder}
        onRouteInfoChange={onRouteInfoChange}
      />
    </Suspense>
  );
};

export default OfflineDispatchMapSurface;
